import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { appConfig } from '~/app.config';
import { AbstractFormImageSelect } from '~/component/form/image/selects/abstract-select/abstract-select';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { FormControlService } from '~/core/services/form/form-control.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { OrganizerSharedLayoutEditFieldComponent } from '~/organizer/shared/components/shared/layouts/edit-field/edit-field.component';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';

interface SportOfferInterface {
  assetIdentifier: string;
  description: string;
  identifier: number;
  image: string;
  name: string;
}

@Component({
  selector: 'app-organizer-activity-edition-formule',
  templateUrl: './formule.component.html',
  styleUrls: ['./formule.component.scss'],
})
export class OrganizerActivityEditionFormuleComponent
  extends AbstractFormImageSelect
  implements OnInit, OnDestroy
{
  @ViewChild(OrganizerSharedLayoutEditFieldComponent)
  public layout: OrganizerSharedLayoutEditFieldComponent;

  private readonly subscriptions = new Subscription();

  public readonly defaultMaximumCharacters =
    appConfig.formValidationRules.activityTitleMaximumLength;
  public readonly variantTips = TipsVariantEnum.INFO;

  public activity;
  public isFormValid: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;

  private offerIdentifier: string;
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  private formule: any;
  public price: FormControl;
  public title: FormControl;
  public description: FormControl;
  public isTabletDevice: boolean;
  public sports: SportOfferInterface[];
  public sportTitle = [];
  public sportDescription = [];
  public photo = [];

  public readonly imageIcon = VtmnPlayIconTypeEnum.Image;
  public readonly plusIcon = VtmnPlayIconTypeEnum.Add;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly locatableService: OrganizerSharedLocatableService,
    private readonly matchMediaService: MatchMediaService,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService,
    private readonly formControlService: FormControlService
  ) {
    super();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.offerIdentifier = this.activatedRoute?.snapshot?.params?.slug;

    this.title = new FormControl('', [
      Validators.required,
      Validators.maxLength(this.defaultMaximumCharacters),
      noHtmlTagValidator(),
    ]);

    this.description = new FormControl('', [
      Validators.required,
      Validators.maxLength(this.defaultMaximumCharacters),
      noHtmlTagValidator(),
    ]);

    this.price = new FormControl('', [Validators.required, Validators.min(0)]);

    this.subscriptions.add(
      this.title.valueChanges.subscribe(() => {
        this.isFormValid = this.title.valid && this.title.dirty;
      })
    );

    this.subscriptions.add(
      this.description.valueChanges.subscribe(() => {
        this.isFormValid = this.description.valid && this.description.dirty;
      })
    );

    this.subscriptions.add(
      this.matchMediaService.isTabletDevice().subscribe((isTabletDevice) => {
        this.isTabletDevice = isTabletDevice;
      })
    );

    this.subscriptions.add(
      this.activatedRoute.data
        .pipe(
          mergeMap(({ activityData }) => {
            return this.organizerSharedActivityService.getActivity(
              activityData.activity?.sku
            );
          })
        )
        .subscribe((activity) => {
          this.activity = activity;
          this.formule = this.activity.indicativeOffers.filter(
            (offer) => offer.identifier === this.offerIdentifier
          )?.[0];
          this.title.setValue(this.formule.name);
          this.description.setValue(this.formule.description);
          this.price.setValue(this.formule.price);
          this.sports =
            (this.formule.meta?.sports as unknown as SportOfferInterface[]) ||
            [];

          this.sports.forEach((_sport) => {
            const title = new FormControl('', [
              Validators.required,
              Validators.maxLength(this.defaultMaximumCharacters),
              noHtmlTagValidator(),
            ]);
            title.setValue(_sport.name);
            this.sportTitle.push(title);

            const description = new FormControl('', [
              Validators.required,
              Validators.maxLength(this.defaultMaximumCharacters),
              noHtmlTagValidator(),
            ]);
            description.setValue(_sport.description);
            this.sportDescription.push(description);

            const media = new FormControl('', Validators.required);
            media.setValue(this.getSportImage(_sport.assetIdentifier));
            this.photo.push(media);
          });
        })
    );
  }

  public checkFormValidity(index): void {
    this.isFormValid = this.photo[index].valid && this.photo[index].dirty;
  }

  public getSportImage(assetIdentifier: string): string {
    return this.activity.getMediaUrlByIdentifier(
      assetIdentifier,
      ActivityMediaResolutionEnum.LARGE
    );
  }

  public get currentCharacters(): number {
    return this.title.value?.length ?? 0;
  }

  public get hasInitialValue(): boolean {
    return Boolean(this.activity?.name);
  }

  public errorMessages(inputName: string): string[] {
    return this.formControlService.getErrorMessages(this.title, inputName);
  }

  public updateFormule(): void {
    this.isOverlayDisplayed = true;

    const sports = [];

    this.sports.forEach((sport, index) => {
      const sportToSend: SportOfferInterface = {
        assetIdentifier: sport.assetIdentifier,
        description: this.sportDescription[index].value,
        identifier: sport.identifier,
        image: this.photo[index].value,
        name: this.sportTitle[index].value,
      };
      sports.push(sportToSend);
    });

    const data = {
      identifier: this.title.value
        .toString()
        .trim()
        .toLowerCase()
        .split(' ')
        .join('-'),
      price: this.price.value,
      currency: this.formule?.currency,
      name: [
        this.locatableService.buildCurrentLocatableBody(
          String(this.title.value)
        ),
      ],
      description: [
        this.locatableService.buildCurrentLocatableBody(
          String(this.description.value)
        ),
      ],
      meta: {
        maximumSportSelectable: Number(
          this.formule?.meta?.maximumSportSelectable
        ),
        sports: sports,
      },
    };

    const offers = this.activity.indicativeOffers.slice();
    const offerIndex = offers.indexOf(this.formule);

    offers[offerIndex] = data;

    offers.forEach((_offer, index) => {
      if (index === offerIndex) {
        return;
      }

      _offer.name = [
        this.locatableService.buildCurrentLocatableBody(String(_offer.name)),
      ];
      _offer.description = [
        this.locatableService.buildCurrentLocatableBody(
          String(_offer.description)
        ),
      ];
    });

    this.organizerSharedActivityService
      .patchActivityById(this.activity.sku, {
        indicativeOffers: [...offers],
      })
      .pipe(
        mergeMap(() =>
          this.organizerSharedActivityService
            .getActivity(this.activity?.sku?.toString())
            .pipe(
              repeatUntil(
                () => this.offerIdentifier === this.formule?.identifier
              )
            )
        )
      )
      .subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      });
  }
}
